import type { Seat } from '@eventflow/shared-types';
import { useEffect, useRef } from 'react';

interface TableSeatPickerProps {
  isOpen: boolean;
  tableId: string | null;
  tableLabel: string;
  seats: Seat[];
  selectedSeatIds: string[];
  onSelectSeat: (seat: Seat) => void;
  onClose: () => void;
  lockingSeats?: Set<string>;
  sessionId?: string;
  onWarn?: (message: string) => void;
  recentlyReleasedSeatIds?: Set<string>;
}

const statusColor = (seat: Seat, selected: boolean, isBeingReleased: boolean, isMine: boolean) => {
  if (selected) return '#10B981'; // Verde - Seleccionado
  if (isBeingReleased || (seat.status === 'locked' && isMine)) return '#22D3EE'; // Cyan - Optimista o lock propio
  switch (seat.status) {
    case 'available':
      return '#22D3EE';
    case 'locked':
      return '#EAB308';
    case 'sold':
      return '#6B7280';
    case 'blocked':
      return '#EF4444';
    default:
      return '#9CA3AF';
  }
};

export default function TableSeatPicker({
  isOpen,
  tableId,
  tableLabel,
  seats,
  selectedSeatIds,
  onSelectSeat,
  onClose,
  lockingSeats = new Set(),
  sessionId,
  onWarn,
  recentlyReleasedSeatIds,
}: TableSeatPickerProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // TEMPORALMENTE DESACTIVADO: Detectar clic fuera del componente para cerrarlo
  /*
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    // Pequeño delay para evitar que se cierre inmediatamente al abrir
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);
  */

  // Ordenar asientos de forma numérica ascendente por el sufijo (01, 02, 03... 12)
  const sortedSeats = [...seats].sort((a, b) => {
    const getSuffix = (identifier: string) => identifier.slice(-2);
    const suffixA = Number.parseInt(getSuffix(a.seat_identifier), 10);
    const suffixB = Number.parseInt(getSuffix(b.seat_identifier), 10);
    return suffixA - suffixB;
  });

  // Función interna para renderizar un botón de asiento
  function SeatButton({
    seat,
    rotated = false,
    index,
  }: { seat: Seat; rotated?: boolean; index: number }) {
    const isSelected = selectedSeatIds.includes(seat.id);
    const isProcessing = lockingSeats.has(seat.id);
    const isBeingReleased =
      (isProcessing && !isSelected) || (recentlyReleasedSeatIds?.has(seat.id) ?? false);
    const isMine = seat.locked_by_session === sessionId;
    const color = statusColor(seat, isSelected, isBeingReleased, isMine);
    const isLockedByOther = seat.status === 'locked' && !isMine && !isSelected;
    const disabled = seat.status === 'sold' || seat.status === 'blocked';
    // Extraer el número del seat_identifier (ej: "MESA-2-09" -> "09")
    const seatNumber = seat.seat_identifier.split('-').pop() || '00';
    const label = seatNumber;

    // Log para debug de cada asiento renderizado
    console.log(`🪑 [SeatButton] Renderizando asiento:`, {
      index,
      visualLabel: label,
      realIdentifier: seat.seat_identifier,
      seatId: seat.id,
      status: seat.status,
      isSelected,
      isProcessing,
    });

    const handleClick = () => {
      console.log(`🖱️ [SeatButton] Click en asiento:`, {
        index,
        visualLabel: label,
        realIdentifier: seat.seat_identifier,
        seatId: seat.id,
        isLockedByOther,
        disabled,
        isProcessing,
        willSelect: !isLockedByOther && !disabled && !isProcessing,
      });

      if (disabled) {
        if (seat.status === 'blocked') {
          if (onWarn) onWarn('Este asiento está bloqueado permanentemente por el organizador');
          else alert('Este asiento está bloqueado permanentemente por el organizador');
        } else if (seat.status === 'sold') {
          if (onWarn) onWarn('Este asiento ya fue vendido');
          else alert('Este asiento ya fue vendido');
        }
        return;
      }
      if (isLockedByOther) {
        if (onWarn) onWarn('Este asiento ya está siendo seleccionado por otro usuario');
        else alert('Este asiento ya está siendo seleccionado por otro usuario');
        return;
      }
      if (!disabled && !isProcessing) {
        console.log(`✅ [SeatButton] Seleccionando asiento:`, {
          visualLabel: label,
          realIdentifier: seat.seat_identifier,
          seatId: seat.id,
        });
        onSelectSeat(seat);
      }
    };

    const visualDisabled = disabled || isLockedByOther || isProcessing;

    // Debug final antes de renderizar
    if (
      seat.seat_identifier === 'M2-06' ||
      seat.seat_identifier === 'M2-01' ||
      seat.seat_identifier === 'M2-12'
    ) {
      console.log(`🖼️ [Render] ${seat.seat_identifier} renderizando con label: "${label}"`);
    }

    return (
      <button
        onClick={handleClick}
        disabled={disabled || isProcessing}
        className={`h-9 w-9 rounded text-xs font-bold border-2 flex items-center justify-center ${
          visualDisabled ? 'cursor-not-allowed opacity-60' : 'hover:opacity-90 hover:scale-105'
        } transition-all`}
        style={{
          backgroundColor: color,
          borderColor: '#0891b2',
          color: '#fff',
          transform: rotated ? 'rotate(45deg)' : 'none',
        }}
        title={seat.seat_identifier}
      >
        <span style={{ transform: rotated ? 'rotate(-45deg)' : 'none', display: 'block' }}>
          {label}
        </span>
      </button>
    );
  }

  // Organizar asientos en forma octagonal usando CSS Grid
  const renderDiamondLayout = () => {
    const seatsForLayout = sortedSeats.slice(0, 12);

    // Grid positions para cada asiento (fila / columna)
    // Layout deseado según imagen: 07-06 arriba, 05-04-03-02 derecha (de arriba a abajo), 01-12 abajo, 11-10-09-08 izquierda
    const gridPositions = [
      { row: 6, col: 4 }, // 01 - abajo derecha
      { row: 5, col: 5 }, // 02 - derecha abajo (intercambiado con 05)
      { row: 4, col: 6 }, // 03 - derecha centro-abajo
      { row: 3, col: 6 }, // 04 - derecha centro-arriba
      { row: 2, col: 5 }, // 05 - derecha arriba (intercambiado con 02)
      { row: 1, col: 4 }, // 06 - arriba derecha
      { row: 1, col: 3 }, // 07 - arriba izquierda
      { row: 2, col: 2 }, // 08 - izquierda arriba
      { row: 3, col: 1 }, // 09 - izquierda centro-arriba
      { row: 4, col: 1 }, // 10 - izquierda centro-abajo
      { row: 5, col: 2 }, // 11 - izquierda abajo
      { row: 6, col: 3 }, // 12 - abajo izquierda
    ];

    return (
      <div
        className="w-full mx-auto"
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(6, 1fr)',
          gridTemplateRows: 'repeat(6, 1fr)',
          width: '230px',
          height: '230px',
          placeItems: 'center',
          gap: '0px',
          margin: '20px auto',
        }}
      >
        {/* Centro - Nombre de la mesa */}
        <div
          style={{
            gridColumn: '3 / 5',
            gridRow: '3 / 5',
            zIndex: 10,
          }}
          className="flex items-center justify-center"
        >
          <div className="px-8 py-4 bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-lg shadow-xl border-2 border-blue-400">
            <div className="text-2xl font-bold">{tableLabel || tableId}</div>
          </div>
        </div>

        {/* Asientos rotados alrededor */}
        {seatsForLayout.map((seat, index) => {
          const pos = gridPositions[index];
          return (
            <div
              key={seat.id}
              style={{
                gridArea: `${pos.row} / ${pos.col}`,
              }}
            >
              <SeatButton seat={seat} rotated={true} index={index} />
            </div>
          );
        })}
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div
      ref={containerRef}
      className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 mb-4"
    >
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-gray-900">Selecciona tus asientos</h3>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 transition-colors"
          aria-label="Cerrar"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>

      <div className="flex flex-col items-center">{renderDiamondLayout()}</div>
    </div>
  );
}
